import { useSelector } from "react-redux";
import { Document } from "types/Document";
import { PermissionModalMode } from "types/PermissionModalMode";
import { Role } from "types/Role";
import { TreeViewObject } from "types/TreeViewObject";
import { User } from "types/User";

export const explodeIndicatorId = (id: string) => {

  const [name, targetId, subTargetId, actionId, indicatorId, sourceId] = id.split("-");
  return { targetId, subTargetId, actionId, indicatorId, sourceId };
}

export const explodeIndicatorIdIndex = (id: string) => {
  const [name, targetIndex, subTargetId, actionId, indicatorId] = id.split("-");
  return { targetIndex, subTargetId, actionId, indicatorId };
}

export const getUrl = (documentId: string, path: string, fileName: string) => {
  // toggle the first "-" segment
  const { targetId, subTargetId, actionId, indicatorId } = explodeIndicatorId(path);
  const tmp = `public/documents/${documentId}/targets/${targetId}/subTargets/${subTargetId}/actions/${actionId}/indicators/${indicatorId}/attachments/${fileName}`;
  return `${process.env.REACT_APP_API_ENDPOINT_local}${tmp}`;
}

export const indicatorIdLastSegment = (id: string) => {
  return id.split("-").pop();
}

export const getIndicatorsIndexList = (data: any, prefix: string = "") => {
  //retrive all the keys from the object data but keys must start with prefix and value associated is true
  const filteredKeys = Object.keys(data).filter((key) => key.startsWith(prefix) && data[key] === true);
  const res = []

  for (let i = 0; i < filteredKeys.length; i++) {
    res.push(indicatorIdLastSegment(filteredKeys[i]));
  }

  return res;
}


export const generateDefaultIdIndicatorsPermissions = (data: any, prefix: string = "") => {

  const res: any = {}

  for (let i = 0; i < data.length; i++) {
    const key: string = `${prefix}-${data[i]}`;
    res[key] = true;
  }

  return res;

}

export const generateBothDefaultIdIndicatorsPermission = (rolePermissionsValues: string[], rolePermissionsAttachments: string[]) => {

  const res =
  {
    ...generateDefaultIdIndicatorsPermissions(rolePermissionsValues, "editValueIndicator"),
    ...generateDefaultIdIndicatorsPermissions(rolePermissionsAttachments, "editAttachmentsIndicator"),
  };

  return res;
}


export const checkIfIndicatorIsInRolesArray = (documentId: string, subtargetId: string, indicatorId: string, roles: any[], isValue = true, isAdmin = false): boolean => {

  const adminRole = isAdmin;
  if (adminRole) {
    return true;
  }

  for (let i = 0; i < roles.length; i++) {
    const role = roles[i];
    const rolePermissions = role.permissions;
    const documentIndex = rolePermissions?.findIndex((item: any) => item.documentId === documentId);

    if (documentIndex === -1 || documentIndex === undefined) {
      return false;
    }

    const subTargetIndex = rolePermissions[documentIndex].subtargets.findIndex((item: any) => item.subtargetId === subtargetId);
    if (subTargetIndex === -1 || documentIndex === undefined) {
      return false;
    }

    if (isValue) {
      const checkExistence = rolePermissions[documentIndex].subtargets[subTargetIndex].permissionsValues?.includes(indicatorId);
      if (checkExistence) {
        return true;
      }
    } else {
      const checkExistence = rolePermissions[documentIndex].subtargets[subTargetIndex].permissionsAttachments?.includes(indicatorId);
      if (checkExistence) {
        return true;
      }
    }
  }
  return false;

}

export const checkIfIndicatorIsInRolePermissions = (documentId: string, subtargetId: string, indicatorId: string, permissions: any, isValue = true): boolean => {

  const documentIndex = permissions?.findIndex((item: any) => item.documentId === documentId);

  if (documentIndex === -1 || documentIndex === undefined) {
    return false;
  }

  const subTargetIndex = permissions[documentIndex].subtargets.findIndex((item: any) => item.subtargetId === subtargetId);
  if (subTargetIndex === -1 || documentIndex === undefined) {
    return false;
  }

  if (isValue) {
    const checkExistence = permissions[documentIndex].subtargets[subTargetIndex].permissionsValues?.includes(indicatorId);
    if (checkExistence) {
      return true;
    }
  } else {
    const checkExistence = permissions[documentIndex].subtargets[subTargetIndex].permissionsAttachments?.includes(indicatorId);
    if (checkExistence) {
      return true;
    }
  }

  return false;


}



export const isCodeInUserRoles = (roleId: string, userId: string, users: User[], modalMode: PermissionModalMode = PermissionModalMode.Create): boolean => {

  if (modalMode === PermissionModalMode.Create) return false;

  //extract roles of the user from the store
  const user = users.find((item: User) => item._id === userId);
  const roles = user?.roles;
  if (!roles) return false;

  for (let i = 0; i < roles.length; i++) {
    if (roles[i] === roleId) {
      return true;
    }
  }

  return false;
}

export const getRolesCodesFromCheckboxes = (data: any): string[] => {
  const filteredKeys = Object.keys(data).filter((key) => data[key] === true)
  return filteredKeys
}

export const truncateString = (str: string, num: number = 40) => {
  if (!str) return "";
  if (str.length <= num) {
    return str
  }
  return str.slice(0, num) + "..."
}

export const formatDatetime = (utfString: string) => {
  // Parse the input string into a Date object
  const date = new Date(utfString);

  // Create an array of month names
  const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
  ];

  // Get the individual components of the date
  const year = date.getFullYear();
  const month = months[date.getMonth()];
  const day = date.getDate();
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  const ampm = hours >= 12 ? 'PM' : 'AM';

  // Convert hours from 24-hour to 12-hour format
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'

  // Format minutes and seconds to always have two digits
  const minutesStr = minutes < 10 ? '0' + minutes : minutes;
  const secondsStr = seconds < 10 ? '0' + seconds : seconds;

  // Construct the readable string
  const readableString = `${month} ${day}, ${year} at ${hours}:${minutesStr}:${secondsStr} ${ampm}`;

  return readableString;
}


export function updateIndexString(input: string, newSegment: string): string {
  const segments = input.split('.');
  segments.pop();
  segments.push(newSegment);
  const res = segments.join('.');
  return res;
}

export function fromDocumentToTreeViewObject(document: Document, timePeriod: string): any {

  const res = document.targets.map((target) => {
    const subTargets = target.subtargets.map((subTarget) => {
      const actions = subTarget.actions.map((action) => {
        const indicators = action.indicators.map((indicator) => {
          return {
            name: indicator.title,
            metadata: {
              is_filled: indicator.values && indicator.values[`${timePeriod}`] !== undefined && indicator.values[`${timePeriod}`] !== "" ? true : false,
              element: "Indicatore",
              index: indicator.index
            },
            children: []
          }
        })
        return {
          name: action.title,
          metadata: { is_filled: indicators.find((indicator) => indicator.metadata.is_filled == false) ? false : true, element: "Azione", index: action.index },
          children: indicators
        }
      })
      return {
        name: subTarget.title,
        metadata: { is_filled: actions.find((action) => action.metadata.is_filled == false) ? false : true, element: "Sotto-obiettivo", index: subTarget.index },
        children: actions
      }
    })
    return {
      name: target.title,
      metadata: {
        is_filled: subTargets.find((subTarget) => subTarget.metadata.is_filled == false) ? false : true, element: "Obiettivo", index: target.index,
        number_of_indicators: subTargets.reduce((acc, subTarget) => acc + subTarget.children.reduce((acc, action) => acc + action.children.length, 0), 0),
        number_of_filled_indicators: subTargets.reduce((acc, subTarget) => acc + subTarget.children.reduce((acc, action) => acc + action.children.filter((indicator) => indicator.metadata.is_filled).length, 0), 0)
      },
      children: subTargets
    }
  })

  return {
    name: document.title,
    metadata: {
      is_filled: res.find((target) => target.metadata.is_filled == false) ? false : true, element: "Root", index: document.code,
    },
    children: res
  };
}

type IndicatorData = {
  key: string;
  value: string;
};

export function groupByIndicatorId(data: Record<string, string>): Record<string, IndicatorData[]> {
  const dataArray: IndicatorData[] = Object.keys(data).map(key => ({
    key,
    value: data[key]
  }));

  const grouped = dataArray.reduce((acc, item) => {
    const { indicatorId } = explodeIndicatorId(item.key);
    if (!acc[indicatorId]) {
      acc[indicatorId] = [];
    }
    acc[indicatorId].push(item);
    return acc;
  }, {} as Record<string, IndicatorData[]>);

  return grouped;
}

export function groupByIndicator(data: any, sources : any) {

  const res = [];
  const grouped = groupByIndicatorId(data);
  for (const key in grouped) {

    const values = grouped[key];
    const obj = {
      indicatorId: key,
      values: {} as Record<string, string>
    };

    for (const value of values) {
      const { sourceId } = explodeIndicatorId(value.key);
      // retrieve the source code
      const source = sources.find((source: any) => source._id === sourceId);
      const sourceCode = source.code;
      
      obj.values[sourceCode] = value.value;
    }

    res.push(obj);
  }


  return res;
}
